import { useLayoutEffect, useState } from "react";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { useIsUserServiceAdminForLocation } from "../../../../hooks/Remote/Admin/useIsUserServiceAdminForLocation";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { MRT_RowSelectionState } from "material-react-table";
import { useTranslation } from "react-i18next";
import { OrderRequestCardItem } from "./OrderRequestCardItem.partial";
import { DetailedServiceBookingData, OrderRequestData } from "../Domain/RemoteOrderRequests.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { OrderRequestDetailCateringTable } from "./OrderRequestDetail-CateringTable.partial";
import { OrderRequestDetailBookableEquipmentTable } from "./OrderRequestDetail-BookableEquipmentTable.partial";
import { OrderRequestDetailRoomLayoutTable } from "./OrderRequestDetail-RoomLayoutTable.partial";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";
import { saveDetailData } from "../functions/saveDetailData.functions";
import { checkCateringDetailError } from "../functions/handleCateringDetailData.function";
import { computeHasNewlyApprovedServiceOrder } from "../functions/hasNewlyApprovedServiceOrder.function";
import {
  resolveOrderRequestDetailListOpen,
  resolveOrderRequestDetailSections
} from "../functions/orderRequestDetailSections.function";
import { useRemoteFetchRoomlayout } from "../../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import { OrderRequestApproveAssociatedBookingDialog } from "./OrderRequestApproveAssociatedBookingDialog.partial";

export type UpdatedServiceOrderRequest = Pick<
  DetailedServiceBookingData,
  "caterings" | "equipments" | "roomLayouts"
>;

export type DetailDataChanges = {
  type: ServiceType;
  id: number;
  originalQty: number;
  newQty: number;
  status: BookingRequestsStatus;
};

type Props = {
  selectedOrder: OrderRequestData;
  /** static data of detail service booking data */
  detailData: DetailedServiceBookingData;
  isDetailDialogOpen: boolean;
  isArchive: boolean;
  isBookingCancelled: boolean;
  isPostLoading: boolean;
  closeDialog: () => void;
  updateOrderRequest: (
    targetBookingId: string,
    changedDeliveryStatus: ServiceDeliveryStatus,
    changes: UpdatedServiceOrderRequest
  ) => Promise<void>;
  approveAssociatedBooking?: () => Promise<void>;
};

export type CateringQuantityErrorType = "min" | "max" | "empty" | null;

export function OrderRequestDetailDialog({
  selectedOrder,
  detailData,
  isDetailDialogOpen,
  isArchive,
  isBookingCancelled,
  isPostLoading,
  closeDialog,
  updateOrderRequest,
  approveAssociatedBooking
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const commonChipStyle = {
    mx: 0,
    my: 0.2,
    width: 200,
    justifyContent: "flex-start",
    pl: 0.5,
    color: "black"
  };

  const [listOpen, setListOpen] = useState<{ [key: number]: boolean }>({
    [0]: false, // catering
    [1]: false, // bookableEquipment
    [2]: false // roomLayout
  });

  /** data gathering of all edited changes (seed from props so first paint matches detailData) */
  const [editedData, setEditedData] = useState<DetailedServiceBookingData | null>(() => detailData);
  /** edited record(quantity) from the each table */
  const [editedRecord, setEditedRecord] = useState<DetailDataChanges[]>([]);

  const [cateringRowSelection, setCateringRowSelection] = useState<MRT_RowSelectionState>({});
  const [equipmentRowSelection, setEquipmentRowSelection] = useState<MRT_RowSelectionState>({});
  const [roomlayoutRowSelection, setRoomlayoutRowSelection] = useState<MRT_RowSelectionState>({});

  const [cateringQuantityErrors, setCateringQuantityErrors] = useState<
    Record<number, CateringQuantityErrorType>
  >({});
  const [isApproveBookingConfirmOpen, setIsApproveBookingConfirmOpen] = useState(false);

  const { data: remoteRoomLayout, isFetched: isRemoteRoomlayoutFetched } =
    useRemoteFetchRoomlayout(true);

  const { sub: userId } = useSelector((state: RootState) => state.login.userInformation);
  const isRequestAdmin = useSelector((state: RootState) => state.login.userRoles.isRequestAdmin);
  const { data: isServiceAdminForLocation } = useIsUserServiceAdminForLocation(
    detailData.locationInventoryId ?? undefined,
    userId,
    isRequestAdmin
  );
  const isServiceAdminBypass = isServiceAdminForLocation === true;
  const isOrderDetailReadOnly = isArchive || isBookingCancelled;
  const detailSections = resolveOrderRequestDetailSections(editedData ?? detailData);
  const isResourceCateringOrder =
    detailSections.isResourceOrder && detailSections.resourceKind === "catering";
  const isResourceEquipmentOrder =
    detailSections.isResourceOrder && detailSections.resourceKind === "equipment";

  const handleChangeStatus = (event: SelectChangeEvent) => {
    if (!editedData) return;

    editedData.deliveryStatus = event.target.value as ServiceDeliveryStatus;
    setEditedData({
      ...editedData,
      deliveryStatus: event.target.value as ServiceDeliveryStatus
    });
  };

  const saveOrderChanges = () => {
    saveDetailData({
      state: {
        editedData,
        cateringRowSelection,
        equipmentRowSelection,
        roomlayoutRowSelection,
        editedRecord
      },
      actions: { updateOrderRequest, setEditedData }
    });
  };

  const shouldAskApproveAssociatedBooking =
    detailData.status !== BookingRequestsStatus.APPROVED &&
    computeHasNewlyApprovedServiceOrder(editedData, editedRecord, detailData) &&
    approveAssociatedBooking;

  const handleConfirmDetailData = () => {
    if (shouldAskApproveAssociatedBooking) {
      setIsApproveBookingConfirmOpen(true);
      return;
    }

    saveOrderChanges();
  };

  const handleConfirmSaveWithBookingApproval = () => {
    setIsApproveBookingConfirmOpen(false);
    approveAssociatedBooking?.().finally(saveOrderChanges);
  };

  const handleConfirmSaveWithoutBookingApproval = () => {
    setIsApproveBookingConfirmOpen(false);
    saveOrderChanges();
  };

  /** store the quantity changes of each service item */
  const handleDetailData = (props: DetailDataChanges) => {
    const { type, id, originalQty, newQty, status } = props;

    // Validate quantity only for catering type
    if (type === ServiceType.Catering) {
      const error = checkCateringDetailError(props, editedData, isResourceCateringOrder);
      setCateringQuantityErrors(prev => ({ ...prev, [id]: error }));
    }

    /* istanbul ignore next */
    setEditedRecord(prev => {
      const i = prev.findIndex(item => item.id === id && item.type === type);
      if (i >= 0) {
        prev[i].newQty = newQty;
        prev[i].status = status;
        return [...prev];
      } else {
        return [...prev, { type, id, originalQty, newQty, status }];
      }
    });

    if (type !== ServiceType.Catering || !editedData) {
      return;
    }

    setEditedData(prev => {
      if (!prev) {
        return prev;
      }

      const caterings = prev.caterings.map(cat => {
        if (cat.id !== id) {
          return cat;
        }

        if (isResourceCateringOrder) {
          return { ...cat, orderedQty: newQty, quantity: newQty, status };
        }

        return { ...cat, quantity: newQty, status };
      });

      return { ...prev, caterings };
    });
  };

  useLayoutEffect(() => {
    setEditedData(detailData);
    setEditedRecord([]);
    setCateringRowSelection({});
    setEquipmentRowSelection({});
    setRoomlayoutRowSelection({});
    setCateringQuantityErrors({});
    setIsApproveBookingConfirmOpen(false);

    setListOpen(resolveOrderRequestDetailListOpen(detailData));
  }, [detailData]);

  return (
    <Dialog open={isDetailDialogOpen} onClose={closeDialog} fullWidth maxWidth={false}>
      {/* header of the dialog */}
      <DialogTitle sx={{ m: 0, p: 2 }}>{t("OrderList_Detail_Title")}</DialogTitle>
      <IconButton
        data-testid="orderRequest-detail-dialog-close-btn"
        onClick={closeDialog}
        sx={{ position: "absolute", right: 8, top: 8, mt: 0.5 }}
      >
        <Close />
      </IconButton>

      <Divider sx={{ width: "99%", alignSelf: "center", opacity: "0.6" }} />

      {/* contents of the dialog */}
      <DialogContent>
        <Grid2 container gap={2} sx={{ height: "80vh" }}>
          {/* selected order request item */}
          <Grid2
            size={{ xs: 3 }}
            data-testid="simple-part-of-detail-item"
            sx={[isMobile ? { display: "none" } : { display: "initial" }]}
          >
            <OrderRequestCardItem
              item={selectedOrder}
              isSelected={false}
              isDetailLoading={false}
              filterByType={[]}
              isArchive={isArchive}
              isBookingCancelled={isBookingCancelled}
            />

            {/* change the status of the selected order request item */}
            <Grid2 sx={{ mt: 2 }}>
              <InputLabel>{t("_orderRequest_orderProgressLabel")}</InputLabel>
              <Select
                label={t("_orderRequest_orderProgressLabel")}
                variant={"outlined"}
                data-testid="service-delivery-status-select"
                value={(editedData ?? detailData).deliveryStatus}
                onChange={handleChangeStatus}
                disabled={isOrderDetailReadOnly}
              >
                {Object.values(ServiceDeliveryStatus).map(status => (
                  <MenuItem
                    key={status}
                    value={status}
                    data-testid={`service-delivery-status-select-${status}`}
                  >
                    {t(status)}
                  </MenuItem>
                ))}
              </Select>

              {/* <Select label={t("Cost Center")} variant={"outlined"} value={} /> */}
            </Grid2>
          </Grid2>

          {/* ordered service item list of the selected order request item */}

          <Grid2
            size={"grow"}
            data-testid="list-part-of-detail-list"
            sx={[{ height: "100%" }, isMobile ? { overflow: "" } : { overflow: "overlay" }]}
          >
            <Grid2
              container
              gap={2}
              data-testid="list-part-of-detail-list-parent"
              sx={{ display: "flex", flexDirection: "column" }}
            >
              {/* catering list */}
              {detailSections.showCateringTable && editedData?.caterings && (
                <Collapse in={listOpen[0]} collapsedSize={40}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <OrderRequestDetailCateringTable
                      detailData={detailData}
                      listOpen={listOpen[0]}
                      setListOpen={setListOpen}
                      handleDetailData={handleDetailData}
                      editedData={editedData}
                      cateringRowSelection={cateringRowSelection}
                      setCateringRowSelection={setCateringRowSelection}
                      isArchive={isArchive}
                      isBookingCancelled={isBookingCancelled}
                      isPostLoading={isPostLoading}
                      chipStyle={commonChipStyle}
                      cateringQuantityErrors={cateringQuantityErrors}
                      isServiceAdminBypass={isServiceAdminBypass}
                      isResourceCateringOrder={isResourceCateringOrder}
                    />
                  </Box>
                </Collapse>
              )}

              {detailSections.showCateringTable && detailSections.showEquipmentTable ? (
                <Divider sx={{ ml: 5, opacity: "0.6" }} />
              ) : null}

              {/* bookable equipment list */}
              {detailSections.showEquipmentTable && editedData?.equipments && (
                <Collapse in={listOpen[1]} collapsedSize={40}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <OrderRequestDetailBookableEquipmentTable
                      listOpen={listOpen[1]}
                      setListOpen={setListOpen}
                      detailData={detailData}
                      editedData={editedData}
                      handleDetailData={handleDetailData}
                      equipmentRowSelection={equipmentRowSelection}
                      setEquipmentRowSelection={setEquipmentRowSelection}
                      isArchive={isArchive}
                      isBookingCancelled={isBookingCancelled}
                      isPostLoading={isPostLoading}
                      chipStyle={commonChipStyle}
                      isServiceAdminBypass={isServiceAdminBypass}
                      isResourceEquipmentOrder={isResourceEquipmentOrder}
                    />
                  </Box>
                </Collapse>
              )}

              {detailSections.showEquipmentTable && detailSections.showRoomLayoutTable ? (
                <Divider sx={{ ml: 5, opacity: "0.6" }} />
              ) : null}

              {/* room layout list */}
              {detailSections.showRoomLayoutTable &&
                isRemoteRoomlayoutFetched &&
                editedData?.roomLayouts &&
                editedData.roomLayouts.length > 0 && (
                  <Collapse in={listOpen[2]} collapsedSize={40}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <OrderRequestDetailRoomLayoutTable
                        listOpen={listOpen[2]}
                        setListOpen={setListOpen}
                        detailData={detailData}
                        editedData={editedData}
                        handleDetailData={handleDetailData}
                        roomlayoutRowSelection={roomlayoutRowSelection}
                        setRoomlayoutRowSelection={setRoomlayoutRowSelection}
                        remoteRoomLayout={remoteRoomLayout}
                        isArchive={isArchive}
                        isBookingCancelled={isBookingCancelled}
                        isPostLoading={isPostLoading}
                        chipStyle={commonChipStyle}
                        isServiceAdminBypass={isServiceAdminBypass}
                      />
                    </Box>
                  </Collapse>
                )}

              {detailSections.showRoomLayoutTable ? (
                <Divider sx={{ ml: 5, opacity: "0.6" }} />
              ) : null}
            </Grid2>
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions>
        {!isArchive && (
          <>
            <Button data-testid="handle-close-btn" onClick={closeDialog} color={"secondary"}>
              {t("Cancel")}
            </Button>
            <Button
              data-testid="confirm-detail-data-btn"
              onClick={handleConfirmDetailData}
              disabled={Object.values(cateringQuantityErrors).some(e => e !== null)}
            >
              {t("Confirm")}
            </Button>
          </>
        )}
      </DialogActions>

      <OrderRequestApproveAssociatedBookingDialog
        open={isApproveBookingConfirmOpen}
        onClose={() => setIsApproveBookingConfirmOpen(false)}
        onConfirmWithoutBookingApproval={handleConfirmSaveWithoutBookingApproval}
        onConfirmWithBookingApproval={handleConfirmSaveWithBookingApproval}
      />
    </Dialog>
  );
}
